import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { Supplier } from "@/apis/gql/types";
import {
  ComboBox,
  type ComboOption,
} from "@/components/shared/tables/tableComboBox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useCreateSupplier } from "@/queries/suppliers/create";
import { useGetAllSuppliers } from "@/queries/suppliers/get-all";
type Props = {
  name: string;
  label?: string;
  options: Supplier[];
  autoFocus?: boolean;
  placeholder?: string;
  description?: string;
  fullWidth?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  sideOffset?: number;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: string | React.ReactNode | boolean;
  readOnly?: string | React.ReactNode | boolean;
  enableClear?: boolean;
  createSupplier?: boolean;
  type?: "creditor" | "debtor";
};

export const SupplierDropdownInput = ({
  label,
  name,
  options,
  fullWidth = true,
  placeholder = "",
  description,
  disabled,
  readOnly,
  className,
  sideOffset,
  enableClear = true,
  createSupplier = true,
  onBlur,
  onFocus,
  type,
}: Props) => {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        return (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <SupplierDropdown
                value={field.value}
                onChange={field.onChange}
                options={options}
                fullWidth={fullWidth}
                placeholder={placeholder}
                className={className}
                sideOffset={sideOffset}
                readOnly={readOnly}
                enableClear={enableClear}
                createSupplier={createSupplier}
                onBlur={onBlur}
                onFocus={onFocus}
                disabled={disabled}
                type={type}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

type SupplierDropdownProps = {
  value?: string;
  onChange: (value: string | undefined) => void;
  options: Supplier[];
  placeholder?: string;
  fullWidth?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  sideOffset?: number;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: string | React.ReactNode | boolean;
  readOnly?: string | React.ReactNode | boolean;
  enableClear?: boolean;
  createSupplier?: boolean;
  type?: "creditor" | "debtor";
};

export const SupplierDropdown = ({
  value,
  onChange,
  options,
  fullWidth = true,
  placeholder = "",
  disabled,
  readOnly,
  className,
  sideOffset,
  enableClear = true,
  createSupplier: _createSupplier = true,
  onBlur,
  onFocus,
  type,
}: SupplierDropdownProps) => {
  const { t } = useTranslation();

  const items =
    options?.map((supplier): ComboOption => {
      if (supplier.supplierId === null) {
        return {
          label: "Unbekannt",
          value: "unknown",
          subLabel: supplier?.datevAccountNumber ?? undefined,
          alternativeLabels: [],
        };
      }

      return {
        label: supplier.supplierName ?? "",
        value: supplier.supplierId ?? "unknown",
        subLabel: supplier?.datevAccountNumber ?? undefined,
        alternativeLabels: [],
      };
    }) ?? [];

  const [createSupplier] = useCreateSupplier({ type });
  const { refetch } = useGetAllSuppliers({ type });

  const createSupplierAndRefetch = async (input: string) => {
    try {
      const response = await createSupplier({
        supplierName: input,
        status: "NEW",
      });
      const newSupplierId = response?.data?.createSupplier?.supplierId;
      if (newSupplierId) {
        await refetch();
        return newSupplierId;
      }
    } catch (error) {
      console.error("Failed to create supplier:", error);
    }
  };

  const currentOption = items.find((option) => option.value === value);

  const currentValue = currentOption
    ? {
        label: currentOption.label ?? "",
        value: currentOption.value,
        subLabel: currentOption.subLabel ?? undefined,
        alternativeLabels: [],
      }
    : undefined;

  return (
    <ComboBox
      currentOption={currentValue}
      onChange={onChange}
      items={items}
      fullWidth={fullWidth}
      placeholder={placeholder}
      variant={"outline"}
      className={className}
      sideOffset={sideOffset}
      readOnly={readOnly}
      createNew={
        _createSupplier
          ? {
              label:
                type === "debtor"
                  ? t("debtors.create.title")
                  : t("creditors.create.title"),
              onCreateNew: async (input: string) => {
                const supplierId = await createSupplierAndRefetch(input);
                if (supplierId) {
                  onChange(supplierId);
                }
              },
            }
          : undefined
      }
      emptyLabel={
        type === "debtor" ? t("debtors.none_found") : t("creditors.none_found")
      }
      enableClear={enableClear}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
    />
  );
};
